/**
 * 频道页相关接口
 */

import { VERSION } from '@/config/version';
import { post } from './api';
import { API_PATHS } from './apiPaths';
import { ChannelPageRequest, ChannelPageResponse } from '@/types/channel';
import { getCookieAmend, setCookieAmend } from '@/utils/apiUtils';
import { logger } from '@/utils/devUtils';

export async function getChannelPageData(body: ChannelPageRequest): Promise<ChannelPageResponse> {
  const queryParams = {
    country_code: getCookieAmend('country_code') || '',
    vversion_name: VERSION,
  };
  const response = (await post<ChannelPageResponse>(API_PATHS.RPCUnChannelPageData, body, {
    params: queryParams,
  })) as ChannelPageResponse;

  // 同步 country_code
  syncCountryCode(response);

  transformUnifiedPage(response);
  return response;
}

/**
 * 同步接口返回的 country_code 到 Cookie
 * @param response 频道页响应数据
 */
function syncCountryCode(response: ChannelPageResponse): void {
  // 从接口响应中获取 country_code
  const serverCountryCode = response.channel_list?.country_code;

  if (!serverCountryCode) {
    return; // 接口没有返回 country_code，不处理
  }

  // 获取 Cookie 中的 country_code
  const cookieCountryCode = getCookieAmend('country_code');

  // 将数字转为字符串进行比较
  const serverCountryCodeStr = String(serverCountryCode);

  if (!cookieCountryCode) {
    // Cookie 中没有 country_code，设置新值
    setCookieAmend('country_code', serverCountryCodeStr);
    logger.info('[ChannelAPI] 设置 country_code:', serverCountryCodeStr);
  }
}

/**
 * service层给数据加上focusedKey，防止因为虚拟列表销毁组件导致view层的index变化导致焦点错乱
 */
const transformUnifiedPage = (data: ChannelPageResponse) => {
  if (!data.unified_page) {
    return data;
  }
  if (!data.unified_page.modules.length) {
    return data;
  }
  // 创建新的数据对象，避免修改原对象
  const newData = { ...data };
  // 只有存在unified_page时才进行处理
  if (newData.unified_page) {
    // 创建新的modules数组，包含带key的模块
    const newModules = newData.unified_page.modules.map((module, moduleIndex) => {
      const moduleWithKey = { ...module, moduleFocusedKey: `${moduleIndex}` };
      // 创建新的sections数组，包含带key的sections
      const newSections = moduleWithKey.sections.map((section, sectionIndex) => {
        const sectionWithKey = { ...section, sectionFocusedKey: `${moduleIndex}-${sectionIndex}` };
        // 创建新的block_list对象，包含带key的blocks
        const newBlocks = sectionWithKey.block_list.blocks.map((block, blockIndex) => {
          const blockWithKey = { ...block, blockFocusedKey: `${moduleIndex}-${sectionIndex}-${blockIndex}` };
          return blockWithKey;
        });
        // 创建新的section对象，包含新的blocks
        return {
          ...sectionWithKey,
          block_list: {
            ...sectionWithKey.block_list,
            blocks: newBlocks,
          },
        };
      });
      // 创建新的module对象，包含新的sections
      return {
        ...moduleWithKey,
        sections: newSections,
      };
    });
    // 创建新的unified_page对象，包含新的modules
    newData.unified_page = {
      ...newData.unified_page,
      modules: newModules,
    };
  }
  return newData;
};
